import { OvForm, OvFormInput, OvFormProvider } from '@components'
import { LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { ArrowBack, ArrowForward, CheckCircle, Logout, Person, Business, Visibility } from '@mui/icons-material'
import {
  Box,
  Button,
  MobileStepper,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  Container,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { MY_COMPANY_DEFAULT_VALUES } from 'src/constants/forms/my-company/my-company.defaultvalues.constants'
import { MyCompanyFormGeneral, WelcomeOnboarding } from 'src/partials/index.partials'

export const OnboardingView = observer(() => {
  const { user, company, auth, ui } = useStore()
  const isMobile = useIsMobile()
  const [activeStep, setActiveStep] = useState(0)
  const navigate = useNavigate()
  const validationSteps = [[], ['profile.name', 'profile.email'], ['company.name']]

  const form = useForm({
    defaultValues: {
      profile: { ...user.current_user },
      company: MY_COMPANY_DEFAULT_VALUES,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { trigger, getValues, setValue } = form

  const ProfileForm = () => (
    <Stack spacing={3}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          variant="h3"
          gutterBottom
          color="primary">
          Vertel ons over jezelf
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary">
          Laten we je profiel instellen zodat je collega&apos;s weten wie je bent
        </Typography>
      </Box>
      <OvFormInput
        name="profile.name"
        label={LABELS.NAME}
        required
        rules={{ required: `${LABELS.NAME} is verplicht` }}
      />
      <OvFormInput
        name="profile.email"
        type="email"
        disabled
        required
        label={LABELS.EMAIL}
        rules={{ required: `${LABELS.EMAIL} is verplicht` }}
      />
    </Stack>
  )

  const CompanyInfoForm = () => {
    return (
      <Stack spacing={3}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h3"
            gutterBottom
            color="primary">
            Je bedrijfsgegevens
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary">
            Vul je bedrijfsgegevens in om facturen te kunnen versturen
          </Typography>
        </Box>
        <MyCompanyFormGeneral />
      </Stack>
    )
  }

  const OnboardingOverview = () => {
    const taxPayable = getValues('company.tax_payable')
    const companyData = getValues('company')
    if (!taxPayable) {
      setValue('company.tax', null)
    }
    return (
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              background: 'linear-gradient(315deg, #311b92 0%, #f50057 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 700,
            }}>
            Controleer je gegevens
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary">
            Ziet alles er goed uit? Dan zijn we klaar om te beginnen!
          </Typography>
        </Box>

        {/* Success Alert */}
        <Box
          sx={{
            p: 2.5,
            background: 'linear-gradient(315deg, rgba(49, 27, 146, 0.08) 0%, rgba(245, 0, 87, 0.08) 100%)',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            borderStyle: 'dashed',
          }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                background: 'linear-gradient(315deg, #311b92 0%, #f50057 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
              <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 0.5 }}>
                Bijna klaar!
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary">
                Controleer onderstaande gegevens en klik op &apos;Account voltooien&apos; om te starten
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Profile Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(49, 27, 146, 0.1)',
            },
          }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}>
            <Person sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600 }}>
              Persoonlijke gegevens
            </Typography>
          </Stack>
          <Stack
            spacing={2}
            sx={{ mt: 2 }}>
            <OvFormInput
              name="profile.name"
              label={LABELS.NAME}
            />
            <OvFormInput
              name="profile.email"
              type="email"
              label={LABELS.EMAIL}
            />
          </Stack>
        </Paper>

        {/* Company Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(49, 27, 146, 0.1)',
            },
          }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ mb: 2 }}>
            <Business sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600 }}>
              Bedrijfsgegevens
            </Typography>
          </Stack>
          <Stack
            spacing={2}
            sx={{ mt: 2 }}>
            <OvFormInput
              name="company.name"
              label={LABELS.COMPANY_NAME}
            />
            {companyData.email && (
              <OvFormInput
                name="company.email"
                label="E-mailadres bedrijf"
              />
            )}
            {companyData.phone && (
              <OvFormInput
                name="company.phone"
                label="Telefoonnummer bedrijf"
              />
            )}
            {companyData.coc && (
              <OvFormInput
                name="company.coc"
                label={LABELS.COC}
              />
            )}
            <OvFormInput
              type="checkbox"
              name="company.tax_payable"
              label="BTW plichtig?"
            />
            {taxPayable && companyData.tax && (
              <OvFormInput
                name="company.tax"
                label={LABELS.TAX_NUMBER}
              />
            )}
          </Stack>
        </Paper>

        {/* Bank Details Section */}
        {(companyData.bank_name || companyData.bank_iban || companyData.bank_bic) && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 4px 12px rgba(49, 27, 146, 0.1)',
              },
            }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2 }}>
              <Business sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600 }}>
                Bankgegevens
              </Typography>
            </Stack>
            <Stack
              spacing={2}
              sx={{ mt: 2 }}>
              {companyData.bank_name && (
                <OvFormInput
                  name="company.bank_name"
                  label="Naam van bank"
                />
              )}
              {companyData.bank_iban && (
                <OvFormInput
                  name="company.bank_iban"
                  label="IBAN"
                />
              )}
              {companyData.bank_bic && (
                <OvFormInput
                  name="company.bank_bic"
                  label="BIC"
                />
              )}
            </Stack>
          </Paper>
        )}

        {/* Address Section */}
        {(companyData.street || companyData.house_number || companyData.postal_code || companyData.city) && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: '0 4px 12px rgba(49, 27, 146, 0.1)',
              },
            }}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ mb: 2 }}>
              <Business sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600 }}>
                Adres
              </Typography>
            </Stack>
            <Stack
              spacing={2}
              sx={{ mt: 2 }}>
              {companyData.street && (
                <OvFormInput
                  name="company.street"
                  label="Straatnaam"
                />
              )}
              {(companyData.house_number || companyData.house_number_addition) && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {companyData.house_number && (
                    <OvFormInput
                      name="company.house_number"
                      label="Huisnummer"
                      sx={{ flex: 2 }}
                    />
                  )}
                  {companyData.house_number_addition && (
                    <OvFormInput
                      name="company.house_number_addition"
                      label="Toevoeging"
                      sx={{ flex: 1 }}
                    />
                  )}
                </Box>
              )}
              {companyData.postal_code && (
                <OvFormInput
                  name="company.postal_code"
                  label="Postcode"
                />
              )}
              {companyData.city && (
                <OvFormInput
                  name="company.city"
                  label="Plaats"
                />
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    )
  }

  const steps = useMemo(
    () => [
      {
        title: 'Welkom',
        component: <WelcomeOnboarding />,
        icon: <CheckCircle />,
      },
      {
        title: 'Profiel',
        component: <ProfileForm />,
        icon: <Person />,
      },
      {
        title: 'Bedrijfsgegevens',
        component: <CompanyInfoForm />,
        icon: <Business />,
      },
      {
        title: 'Controle',
        component: <OnboardingOverview />,
        icon: <Visibility />,
      },
    ],
    []
  )

  const renderSteps = useMemo(() => {
    return steps.map(singleStep => (
      <Step key={singleStep.title}>
        <StepLabel>{singleStep.title}</StepLabel>
      </Step>
    ))
  }, [steps])

  const handleNext = async (e: any) => {
    e.preventDefault()

    // Get the fields to validate for this step
    let fieldsToValidate = validationSteps[activeStep] as any

    // For company step, if tax_payable is true, also validate tax field
    if (activeStep === 2) {
      const taxPayable = getValues('company.tax_payable')
      if (taxPayable) {
        fieldsToValidate = [...fieldsToValidate, 'company.tax']
      }
      // If IBAN is filled in, validate it
      const bankIban = getValues('company.bank_iban')
      if (bankIban && bankIban.length > 0) {
        fieldsToValidate = [...fieldsToValidate, 'company.bank_iban']
      }
    }

    const valid = await trigger(fieldsToValidate)
    if (valid) {
      setActiveStep(prevActiveStep => prevActiveStep + 1)
    } else {
      // Show error message when validation fails
      ui.handleToaster('Vul alle verplichte velden in voordat je verdergaat', { type: 'error' })
    }
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const handleFinish = async (data: any) => {
    const { profile, company: companyData } = data

    try {
      // Validate required company fields (only name is required)
      if (!companyData.name) {
        ui.handleToaster('Bedrijfsnaam is verplicht', { type: 'error' })
        return
      }

      // Update user profile
      await user.update(profile)

      // Create company
      const createdCompany = await company.create(companyData)

      if (createdCompany) {
        ui.handleToaster('Welkom bij Outvoice! Je account is klaar voor gebruik.', { type: 'success' })

        // Reset form to mark it as pristine (not dirty)
        // This prevents the navigation blocker from triggering
        form.reset(
          {
            profile,
            company: createdCompany,
          },
          { keepValues: true }
        )

        // Force a re-check of company status before navigating
        await company.getMyCompany()
        navigate(URL_PATHS.DASHBOARD)
      }
    } catch (e: any) {
      console.error('Onboarding error:', e)
      ui.handleToaster(e?.message || 'Er is iets misgegaan bij het voltooien van de onboarding', { type: 'error' })
    }
  }

  const onFormSubmit = () => {
    form.handleSubmit(handleFinish)()
  }

  const getPageTitle = useMemo(() => {
    return `Welkom, ${user.current_user_name}!`
  }, [user.current_user_name])

  // Check if user already has a company and redirect them
  // TEMPORARY: Disabled to allow testing onboarding
  // useEffect(() => {
  //   const checkExistingCompany = async () => {
  //     try {
  //       await company.getMyCompany()
  //       if (company.has_company && company.current_company?.name) {
  //         // User already has a company, redirect to dashboard
  //         navigate(URL_PATHS.DASHBOARD, { replace: true })
  //       }
  //     } catch (e) {
  //       // User doesn't have a company, stay on onboarding
  //       console.log('No company found, continuing with onboarding')
  //     }
  //   }
  //   checkExistingCompany()
  // }, [])

  return (
    <OvFormProvider
      form={form}
      readOnly={activeStep === steps.length - 1}
      onSubmit={onFormSubmit}>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          width: '100%',
          maxWidth: '100%',
          margin: 0,
          padding: 0,
        }}>
        {/* Header with stepper */}
        <Paper
          elevation={1}
          sx={{ position: 'sticky', top: 0, zIndex: 99, borderRadius: 0, width: '100%' }}>
          <Container
            maxWidth="xl"
            sx={{ py: isMobile ? 1.5 : 2 }}>
            <Stack spacing={isMobile ? 1.5 : 2}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 0.5 }}>
                  {getPageTitle}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary">
                  Stap {activeStep + 1} van {steps.length}
                </Typography>
              </Box>
              {!isMobile && (
                <Stepper
                  activeStep={activeStep}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '0.875rem',
                    },
                    '& .MuiStepIcon-root': {
                      fontSize: '1.5rem',
                    },
                  }}>
                  {renderSteps}
                </Stepper>
              )}
            </Stack>
          </Container>
        </Paper>

        {/* Main content */}
        <Container
          maxWidth="xl"
          sx={{ py: 4 }}>
          <OvForm>
            <Box sx={{ minHeight: 'calc(100vh - 300px)', pb: 11 }}>
              <Paper
                elevation={2}
                sx={{ p: isMobile ? 3 : 5, maxWidth: 900, mx: 'auto' }}>
                {steps[activeStep].component}
              </Paper>
            </Box>

            {/* Footer navigation */}
            {isMobile ? (
              <MobileStepper
                activeStep={activeStep}
                steps={steps.length}
                variant="dots"
                position="bottom"
                elevation={8}
                sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100 }}
                backButton={
                  activeStep === 0 ? (
                    <Tooltip title={TOOLTIPS.LOGOUT_ONBOARDING}>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => auth.handleLogOut()}
                        startIcon={<Logout />}>
                        {LABELS.LOGOUT}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={TOOLTIPS.PREVIOUS_STEP}>
                      <span>
                        <Button
                          size="small"
                          color="inherit"
                          disabled={activeStep === 0}
                          onClick={handleBack}
                          startIcon={<ArrowBack />}>
                          Terug
                        </Button>
                      </span>
                    </Tooltip>
                  )
                }
                nextButton={
                  activeStep !== steps.length - 1 ? (
                    <Tooltip title={TOOLTIPS.NEXT_STEP}>
                      <Button
                        size="small"
                        onClick={(e: any) => handleNext(e)}
                        variant="contained"
                        endIcon={<ArrowForward />}>
                        Volgende
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={TOOLTIPS.COMPLETE_ONBOARDING}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        endIcon={<CheckCircle />}>
                        Voltooien
                      </Button>
                    </Tooltip>
                  )
                }
              />
            ) : (
              <Paper
                elevation={8}
                sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderRadius: 0, zIndex: 100 }}>
                <Container
                  maxWidth="xl"
                  sx={{ py: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {activeStep === 0 ? (
                      <Tooltip title={TOOLTIPS.LOGOUT_ONBOARDING}>
                        <Button
                          color="inherit"
                          onClick={() => auth.handleLogOut()}
                          startIcon={<Logout />}>
                          {LABELS.LOGOUT}
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title={TOOLTIPS.PREVIOUS_STEP}>
                        <span>
                          <Button
                            color="inherit"
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            startIcon={<ArrowBack />}>
                            Terug
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Box sx={{ flex: '1 1 auto' }}></Box>
                    {activeStep !== steps.length - 1 ? (
                      <Tooltip title={TOOLTIPS.NEXT_STEP}>
                        <Button
                          onClick={(e: any) => handleNext(e)}
                          variant="contained"
                          size="large"
                          endIcon={<ArrowForward />}>
                          Volgende
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title={TOOLTIPS.COMPLETE_ONBOARDING}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          color="primary"
                          endIcon={<CheckCircle />}>
                          Account voltooien
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Container>
              </Paper>
            )}
          </OvForm>
        </Container>
      </Box>
    </OvFormProvider>
  )
})
